import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, RefreshCw } from 'lucide-react';
import { fetchCustomerDirectory } from '@/lib/api/customers';
import { rejectionDetail } from '@/lib/api/errors';
import { helpdeskApi, type Account, type HelpdeskConfigCreate } from '@/lib/api/helpdesk';
import { autoMatch, toBindings, type CustomerLinks, type MatchCustomer } from '@/lib/helpdesk/matchAccounts';
import { CustomerAccountMapper } from './CustomerAccountMapper';

type Step = 'provider' | 'credentials' | 'customers';

const PROVIDERS = [
  { id: 'zoho', name: 'Zoho Desk', available: true },
  { id: 'itsm_mcp', name: 'ITSM (Pantheon)', available: true },
  { id: 'servicenow', name: 'ServiceNow', available: false },
  { id: 'zendesk', name: 'Zendesk', available: false },
  { id: 'jira', name: 'Jira Service Management', available: false },
] as const;

/** The ITSM server holds the helpdesk credentials; this dashboard holds a key to it. */
const ITSM = 'itsm_mcp';
const LOOKUP_WARNING = 'Zoho account lookup failed. You can create the integration now and link customers later.';
const DIRECTORY_ERROR = "Couldn't load the customer directory. You can create the integration now and link customers later under Customer accounts.";
const INPUT = 'mt-1 block w-full px-4 py-2 bg-white dark:bg-slate-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const LABEL = 'text-sm font-medium text-gray-700 dark:text-gray-300';
const SECONDARY = 'flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors';
const PRIMARY = 'flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center';

/** Three-step setup wizard: provider → credentials → customer ↔ account mapping. */
export function AddIntegrationModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [step, setStep] = useState<Step>('provider');
  const [formData, setFormData] = useState<Partial<HelpdeskConfigCreate>>({ provider: 'zoho', region: 'eu' });
  const [exchanging, setExchanging] = useState(false);
  const [tokensObtained, setTokensObtained] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountWarning, setAccountWarning] = useState('');
  const [customers, setCustomers] = useState<MatchCustomer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [directoryError, setDirectoryError] = useState(false);
  const [links, setLinks] = useState<CustomerLinks>({});
  const seeded = useRef(false);

  // Entering step 3 loads the customer directory; auto-matches are seeded once per token exchange.
  useEffect(() => {
    if (step !== 'customers') return;
    let active = true;
    setLoadingCustomers(true);
    setDirectoryError(false);
    fetchCustomerDirectory()
      .then(directory => {
        if (!active) return;
        setCustomers(directory.customers);
        if (!seeded.current) {
          setLinks(autoMatch(directory.customers, accounts, {}));
          seeded.current = true;
        }
      })
      .catch(() => { if (active) { setCustomers([]); setDirectoryError(true); } })
      .finally(() => { if (active) setLoadingCustomers(false); });
    return () => { active = false; };
  }, [step, accounts]);

  const resetTokens = () => {
    setTokensObtained(false);
    setConnectionStatus(null);
    setAccounts([]);
    setAccountWarning('');
    seeded.current = false;
    setLinks({});
  };

  const handleGrantTokenExchange = async () => {
    if (!formData.client_id || !formData.client_secret || !formData.grant_token) {
      alert('Please fill in Client ID, Client Secret, and Grant Token');
      return;
    }
    setExchanging(true);
    setConnectionStatus(null);
    try {
      const tokenResult = await helpdeskApi.exchangeCode({
        provider: formData.provider!,
        client_id: formData.client_id,
        client_secret: formData.client_secret,
        code: formData.grant_token,
        grant_type: 'authorization_code',
        region: formData.region,
      });
      if (!tokenResult.success || !tokenResult.access_token) {
        throw new Error(tokenResult.message || tokenResult.error || 'Token exchange failed');
      }
      setFormData(prev => ({ ...prev, access_token: tokenResult.access_token, refresh_token: tokenResult.refresh_token }));
      try {
        const fetched = await helpdeskApi.fetchAccounts({
          provider: formData.provider!,
          client_id: formData.client_id,
          client_secret: formData.client_secret,
          access_token: tokenResult.access_token,
          region: formData.region,
        });
        setAccounts(fetched);
        setAccountWarning('');
        setConnectionStatus({ success: true, message: `Tokens obtained successfully! Found ${fetched.length} accounts. Click Next to continue.` });
      } catch {
        setAccounts([]);
        setAccountWarning(LOOKUP_WARNING);
        setConnectionStatus({ success: true, message: 'Tokens obtained, but the Zoho account lookup failed. You can continue and link customers later.' });
      }
      setTokensObtained(true);
    } catch (error) {
      setConnectionStatus({ success: false, message: error instanceof Error ? error.message : 'Failed to exchange grant token' });
    } finally {
      setExchanging(false);
    }
  };

  const testItsmConnection = async () => {
    setExchanging(true);
    setConnectionStatus(null);
    try {
      const result = await helpdeskApi.testConnection({
        provider: ITSM,
        api_base_url: formData.api_base_url,
        access_token: formData.access_token,
      });
      setConnectionStatus({ success: result.success, message: result.message });
      if (!result.success) return;
      try {
        const fetched = await helpdeskApi.fetchAccounts({
          provider: ITSM,
          api_base_url: formData.api_base_url,
          access_token: formData.access_token,
        });
        setAccounts(fetched);
        setAccountWarning('');
      } catch {
        setAccounts([]);
        setAccountWarning('The account directory could not be read. You can create the integration now and link customers later.');
      }
      setTokensObtained(true);
    } catch (error) {
      setConnectionStatus({ success: false, message: error instanceof Error ? error.message : 'Could not reach the ITSM server' });
    } finally {
      setExchanging(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.provider) {
      alert('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await helpdeskApi.createConfig({ ...(formData as HelpdeskConfigCreate), customer_accounts: toBindings(links, customers) });
      onSuccess();
    } catch (error) {
      const detail = rejectionDetail(error);
      const fallback = error instanceof Error ? error.message : '';
      setSubmitError(detail || fallback || 'Failed to create integration');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add Helpdesk Integration</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Connect your ticketing system to monitor support tickets</p>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4">
            <StepIndicator number={1} label="Provider" active={step === 'provider'} completed={step !== 'provider'} />
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <StepIndicator number={2} label="Credentials" active={step === 'credentials'} completed={step === 'customers'} />
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <StepIndicator number={3} label="Customers" active={step === 'customers'} completed={false} />
          </div>

          {step === 'provider' && (
            <div className="space-y-4">
              <label className="block">
                <span className={LABEL}>Integration Name</span>
                <input type="text" value={formData.name || ''} className={INPUT} placeholder="e.g., Production Helpdesk"
                  onChange={e => setFormData({ ...formData, name: e.target.value })} />
              </label>
              <div className="space-y-2">
                <span className={LABEL}>Select Provider</span>
                <div className="grid grid-cols-2 gap-3">
                  {PROVIDERS.map(provider => (
                    <button key={provider.id} type="button" disabled={!provider.available}
                      onClick={() => setFormData({ ...formData, provider: provider.id })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.provider === provider.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      } ${!provider.available ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <div className="font-semibold text-gray-900 dark:text-white">{provider.name}</div>
                      {!provider.available && <div className="text-xs text-gray-500 mt-1">Coming Soon</div>}
                    </button>
                  ))}
                </div>
              </div>
              <button type="button" onClick={() => setStep('credentials')}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                Next: Enter Credentials
              </button>
            </div>
          )}

          {step === 'credentials' && formData.provider === ITSM && (
            <div className="space-y-4">
              <label className="block">
                <span className={LABEL}>Server URL</span>
                <input type="text" value={formData.api_base_url || ''} className={INPUT} placeholder="http://10.5.136.24:9002"
                  onChange={e => { setFormData({ ...formData, api_base_url: e.target.value }); resetTokens(); }} />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  The ITSM server's base URL. It holds the helpdesk credentials, so none are stored here.
                </p>
              </label>
              <label className="block">
                <span className={LABEL}>API Token</span>
                <input type="password" value={formData.access_token || ''} className={INPUT} placeholder="Bearer k_ext_…"
                  onChange={e => { setFormData({ ...formData, access_token: e.target.value }); resetTokens(); }} />
              </label>

              {connectionStatus && (
                <div className={`p-3 rounded-lg ${
                  connectionStatus.success
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                }`}>
                  {connectionStatus.message}
                </div>
              )}

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep('provider')} className={SECONDARY}>Back</button>
                {tokensObtained && !exchanging ? (
                  <button type="button" onClick={() => setStep('customers')}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center">
                    Next: Map Customers →
                  </button>
                ) : (
                  <button type="button" onClick={testItsmConnection}
                    disabled={exchanging || !formData.api_base_url || !formData.access_token} className={PRIMARY}>
                    {exchanging ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Connecting...</>) : 'Test Connection'}
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 'credentials' && formData.provider !== ITSM && (
            <div className="space-y-4">
              <label className="block">
                <span className={LABEL}>Client ID</span>
                <input type="text" value={formData.client_id || ''} className={INPUT} placeholder="Enter OAuth Client ID"
                  onChange={e => setFormData({ ...formData, client_id: e.target.value })} />
              </label>
              <label className="block">
                <span className={LABEL}>Client Secret</span>
                <input type="password" value={formData.client_secret || ''} className={INPUT} placeholder="Enter OAuth Client Secret"
                  onChange={e => setFormData({ ...formData, client_secret: e.target.value })} />
              </label>
              <label className="block">
                <span className={LABEL}>Grant Token</span>
                <input type="text" value={formData.grant_token || ''} className={INPUT} placeholder="1000.xxxxxxxxxxxxxxxxxxxx"
                  onChange={e => {
                    setFormData({ ...formData, grant_token: e.target.value, access_token: undefined, refresh_token: undefined });
                    resetTokens();
                  }} />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Generate from <a href="https://api-console.zoho.eu/" target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline">Zoho API Console</a> with scope:
                  Desk.basic.READ,profile.userphoto.READ,Desk.tickets.READ,Desk.contacts.READ
                </p>
              </label>
              <label className="block">
                <span className={LABEL}>Region</span>
                <select value={formData.region || 'eu'} className={INPUT}
                  onChange={e => setFormData({ ...formData, region: e.target.value })}>
                  <option value="eu">Europe (EU)</option>
                  <option value="us">United States (US)</option>
                  <option value="in">India (IN)</option>
                  <option value="au">Australia (AU)</option>
                  <option value="cn">China (CN)</option>
                  <option value="jp">Japan (JP)</option>
                </select>
              </label>

              {connectionStatus && (
                <div className={`p-3 rounded-lg ${
                  connectionStatus.success
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                }`}>
                  {connectionStatus.message}
                </div>
              )}

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep('provider')} className={SECONDARY}>Back</button>
                {tokensObtained && !exchanging ? (
                  <button type="button" onClick={() => setStep('customers')}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center">
                    Next: Map Customers →
                  </button>
                ) : (
                  <button type="button" onClick={handleGrantTokenExchange} disabled={exchanging} className={PRIMARY}>
                    {exchanging ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Exchanging Token...</>) : 'Exchange for Tokens'}
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 'customers' && (
            <div className="space-y-4">
              <div>
                <h3 className={LABEL}>Link dashboard customers to Zoho accounts</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Matching names are linked automatically. Select an account for the rest, or leave a customer
                  unlinked if it has no Zoho account. Only linked accounts are collected.
                </p>
              </div>
              {loadingCustomers ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading customers...</span>
                </div>
              ) : directoryError ? (
                <p role="alert" className="text-sm text-amber-700 dark:text-amber-400">{DIRECTORY_ERROR}</p>
              ) : customers.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No customers yet. Customers will appear after AIQ discovery completes; you can link them later
                  under Customer accounts.
                </p>
              ) : (
                <CustomerAccountMapper customers={customers} accounts={accounts} links={links} onChange={setLinks}
                  disabled={submitting} warning={accountWarning || undefined} />
              )}
              {submitError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{submitError}</p>}
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setSubmitError(''); setStep('credentials'); }} className={SECONDARY}>Back</button>
                <button type="button" onClick={handleSubmit} disabled={submitting || loadingCustomers} className={PRIMARY}>
                  {submitting ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin inline" />Creating...</>) : 'Create Integration'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button type="button" onClick={onClose}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function StepIndicator({ number, label, active, completed }: { number: number; label: string; active: boolean; completed: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-colors ${
        completed ? 'bg-green-500 text-white' : active ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
      }`}>
        {completed ? <Check className="w-4 h-4" /> : number}
      </div>
      <span className={`text-sm font-medium ${active ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
        {label}
      </span>
    </div>
  );
}
